/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import _ from "lodash";
import { useRouter } from "next/router";
import { default as React, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { ContactApi } from "../api/Contacts";
import { IntegrationAPI } from "../api/IntegrationAPI";
import { TagApi } from "../api/Tags";
import {
  AllContactsTotalAtom,
  ContactFilterAtom,
  IsCheckedAllContactsAtom,
} from "../atoms/ContactFilterAtom";
import {
  FilterCounterAtom,
  SelectedLabelAtom,
} from "../atoms/ContactFilterAtoms";
import { ContactFiltersAtom } from "../atoms/ContactFiltersAtom";
import { ContactsAtom } from "../atoms/ContactsAtom";
import { ContactsPerPageAtom } from "../atoms/ContactsPerPageAtom";
import {
  ExcludeContactIdsAtom,
  SelectedContactsAtom,
} from "../atoms/SelectedContactsAtom";
import { TagsAtom } from "../atoms/TagsAtom";
import { TestDriveBannerAtom } from "../atoms/TestDriveBannerAtom";
import { UpdateContactsFlagAtom } from "../atoms/UpdateContactsAtom";
import { UserAtom } from "../atoms/UserAtom";
import Header from "../components/Contacts/Header";
import SidebarFilters from "../components/Contacts/SidebarFilters";
import StatusTypesModal from "../components/Contacts/StatusTypesModal";
import TableRows from "../components/Contacts/TableRows";
import TopBar from "../components/Contacts/TopBar";
import IconBerserkerMail from "../components/Icon";
import Loading from "../components/Loading";
import AddContact from "../components/Modal/container/AddContact";
import ImportContact from "../components/Modal/container/ImportContact";
import { AllIntegrationsAtom } from "../components/Settings/Integrations/Integrations";
import StyledButton from "../components/StyledButton";
import StyledCheckBox from "../components/StyledCheckBox";
import { TableFooter } from "../components/StyledTable";
import ContactsSelected from "../components/Toasts/ContactsSelected";
import useClickOutside from "../hook/useClickOutside";
import useRowsPerPage, { ITableName } from "../hook/useRowsPerPage";
import { notSelect } from "../styles/general";
import { ContactStatus } from "../types/Contact";
import { localStorageGetItem } from "../utils/LocalStorage";
import { useModal } from "./../components/Modal/Modal";
import SeoHead from "./../components/SeoHead";

const Flex = styled.div`
  max-width: 100vw;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableContainer = styled.div<{
  isFilterOpen?: boolean;
  showTestDrive?: boolean;
}>`
  width: 100%;
  height: ${(props) => {
    if (props.showTestDrive) return "calc(100vh - 240px)";
    else return "calc(100vh - 165px)";
  }};

  padding: 20px;

  display: flex;
  flex-direction: column;
  flex: 1;

  overflow-y: scroll;
`;

const CheckboxTableTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;

  .e1axkcwe0 {
    min-width: 15px;
    min-height: 15px;
    max-width: 15px;
    max-height: 15px;

    margin: 0 24px 0 0 !important;
  }
`;

const TableCard = styled.div`
  width: 100%;

  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  display: grid;
  grid-template-columns:
    219px 64px 180px minmax(90px, 160px) 120px minmax(110px, 180px)
    60px;
  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    width: fit-content;
  }
`;

const TableHead = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  font-weight: 600;
  cursor: default;
  position: relative;
  ${notSelect}
`;

const TableActions = styled.div`
  color: #707070;
  display: flex;
  font-size: 15px;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  cursor: default;
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f9f9f9;
  padding-top: 84px;

  .loading-container {
    width: 100%;
    height: calc(100vh - 164px);
    min-height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Icon = styled.img`
  width: 10px;
  height: 10px;
  margin-left: 8px;
  ${notSelect}
  cursor: pointer;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const SelectPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  height: fit-content;
  width: 240px;
  overflow: auto;
  border-radius: 5px;
  top: 5;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  display: flex;
  flex-direction: column;
`;

const TitlePopover = styled.div`
  font-size: 13px;
  color: #888888;
  margin-bottom: 5px;
  margin-left: 17px;
  padding-top: 18px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const InfoContainer = styled.div`
  position: relative;
  cursor: pointer;
  margin-left: 2px;
  margin-top: 10px;
  padding-bottom: 10px;

  :hover {
    svg {
      path {
        fill: #fb972e;
      }
    }
  }
`;

const NoContactsWrapper = styled.div`
  width: 100%;
  height: 100%;

  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > div.Title {
    font-size: 24px;
    font-weight: 600;
    color: #222222;

    margin-top: 16px;
    margin-bottom: 8px;
  }

  > div.Text {
    max-width: 560px;

    font-size: 18px;
    font-weight: 400;
    color: #707070;
  }
`;

const divider = css`
  border-bottom: 1px solid #eaeaea;
`;

const PopoverTitle = styled.div`
  color: #888888;
  font-size: 12px;
`;

const Popover = styled.div`
  position: absolute;
  z-index: 999;
  height: fit-content;
  width: max-content;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  display: flex;
  flex-direction: column;
  padding: 20px;
  left: 0px;
  top: 20px;
  background-color: #fff;
  cursor: auto;

  > div {
    margin-bottom: 5px;
  }

  > span {
    color: #333333;
  }
`;

const PopoverClickHere = styled.span`
  color: #fb972e;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export interface FilterOptions {
  status: ContactStatus[];
  labels: string[];
  noLabels: boolean;
}

export const FilterByEmailAtom = atom<string>({
  key: "FilterByEmailAtom",
  default: "",
});

export const StatusAndLabelsFilterContactAtom = atom<FilterOptions>({
  key: "StatusAndLabelsFilterContactAtom",
  default: {
    status: [
      ContactStatus.CONFIRMED,
      ContactStatus.NOT_CONFIRMED,
      ContactStatus.ADMIN_UNSUBSCRIBED,
      ContactStatus.UNSUBSCRIBED,
      ContactStatus.SUPRESSED,
      ContactStatus.SPAM,
      ContactStatus.BOT_FILLED,
      ContactStatus.BLOCKED,
      ContactStatus.PENDING_CONFIRMATION,
    ],
    labels: [],
    noLabels: true,
  },
});

export const ResetSearchAtom = atom<boolean>({
  key: "ResetSearchAtom",
  default: true,
});

export default function ContactsScreen(): JSX.Element {
  const user = useRecoilValue(UserAtom);
  const [contactFilters, setContactFilters] =
    useRecoilState(ContactFiltersAtom);
  const [contacts, setContacts] = useRecoilState(ContactsAtom);
  const [total, setTotal] = useRecoilState(AllContactsTotalAtom);
  const [contactFilter, setContactFilter] = useRecoilState(ContactFilterAtom);
  const filterByEmail = useRecoilValue(FilterByEmailAtom);
  const modal = useModal();
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);
  const [resetSearch, setResetSearch] = useRecoilState(ResetSearchAtom);
  const [page, setPage] = useState(0);
  const countdownToast = React.useRef<React.ReactText | null>(null);
  const [isCheckedAllContacts, setIsCheckedAllContacts] = useRecoilState(
    IsCheckedAllContactsAtom
  );
  const setExcludeContactIds = useSetRecoilState(ExcludeContactIdsAtom);
  const [isCheckedContactsOnPage, setIsCheckedContactsOnPage] = useState(false);
  const [selectedContacts, setSelectedContacts] =
    useRecoilState(SelectedContactsAtom);
  const [openDropView, setOpenDropView] = useState(false);
  const selectCheckboxRef = useClickOutside(() => setOpenDropView(false));
  const [contactsOnPage, setContactsOnPage] =
    useRecoilState(ContactsPerPageAtom);
  const [showCard, setShowCard] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const setTagsPool = useSetRecoilState(TagsAtom);
  const [updateContactsFlag, setUpdateContactFlag] = useRecoilState(
    UpdateContactsFlagAtom
  );
  const [totalFilterAppliedCount, setTotalFilterAppliedCount] = useState(0);
  const [filterCounter, setFilterCounter] = useRecoilState(FilterCounterAtom);
  const changePage = useRef(false);
  const [firstLoading, setFirstLoading] = useState(true);
  const [openStatusInfo, setOpenStatusInfo] = useState(false);
  const isFirstRender = useRef(true);
  const [selectedLabel, setSelectedLabel] = useRecoilState(SelectedLabelAtom);
  const [integrations, setIntegrations] = useRecoilState(AllIntegrationsAtom);

  const { search } = router.query;

  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.CONTACTS
  );
  const [rowsPerPage, setRowsPerPage] = useState(cachedRowsPerPage || 25);

  function handleResetSearch() {
    setResetSearch(!resetSearch);
  }

  function handlePage(vle: number) {
    changePage.current = true;
    setPage(vle);
    setIsCheckedContactsOnPage(false);
    setSelectedContacts([]);
  }

  function resetPage(setPageToZero: boolean) {
    if (setPageToZero) setPage(0);
    else changePage.current = false;
    setUpdateContactFlag(false);
    setSelectedContacts([]);
    setIsCheckedContactsOnPage(false);
    unselectAll();
  }

  function openSelectedContactToast(text: string) {
    countdownToast.current = toast(
      <ContactsSelected text={text} deselect={() => unselectAll()} />,
      {
        position: "bottom-center",
        closeButton: false,
        hideProgressBar: true,
        closeOnClick: false,
        style: {
          backgroundColor: "#333333",
          boxShadow: "0px 3px 8px #0000004d",
          padding: "0px",
          margin: "0px",
          cursor: "auto",
          width: "450px",
        },
      }
    );
  }

  useEffect(() => {
    const totalCount = Object.values(filterCounter).reduce(
      (previousValue, currentValue) => previousValue + currentValue
    );
    setTotalFilterAppliedCount(totalCount);
  }, [filterCounter]);

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  async function getIntegrations() {
    if (integrations.length > 0) return;
    const integrationsResponse = await IntegrationAPI.getAll();
    if (integrationsResponse) setIntegrations(integrationsResponse);
  }

  useEffect(() => {
    getTags();
    getIntegrations();
  }, []);

  // If you select more than one contact, close the contact that is expanded
  useEffect(() => {
    if (selectedContacts.length > 1) setShowCard("");
  }, [selectedContacts]);

  useEffect(() => {
    if (updateContactsFlag) {
      handleResetSearch();
    }
  }, [updateContactsFlag]);

  useEffect(() => {
    if (!user) return;
    const { fromTag, search } = router.query;

    try {
      const newRowsPerPageFromLocalStorage = JSON.parse(
        localStorage.getItem(`@berserker/rows_per_page_${user.email}`)
      );

      const filterFromLocalStorage = JSON.parse(
        localStorage.getItem(`@berserker/contacts_filter_${user.email}`)
      );

      if (filterFromLocalStorage && !fromTag && !search) {
        filterFromLocalStorage.filter &&
          setContactFilters({ ...filterFromLocalStorage.filter });

        filterFromLocalStorage.tagFilter &&
          !_.isEmpty(filterFromLocalStorage.tagFilter) &&
          setContactFilter({ ...filterFromLocalStorage.tagFilter });

        filterFromLocalStorage.filterCounter &&
          setFilterCounter({ ...filterFromLocalStorage.filterCounter });

        filterFromLocalStorage.selectedLabel &&
          setSelectedLabel(filterFromLocalStorage.selectedLabel);
      }

      if (!newRowsPerPageFromLocalStorage?.CONTACTS) return;

      setRowsPerPage(parseInt(newRowsPerPageFromLocalStorage.CONTACTS));
    } catch {
    } finally {
      setTimeout(() => {
        setFirstLoading(false);
      }, 100);
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem(
      `@berserker/contacts_filter_${user.email}`,
      JSON.stringify({
        filter: {
          ...contactFilters,
        },
        tagFilter: {
          ...contactFilter,
        },
        filterCounter: {
          ...filterCounter,
        },
        selectedLabel: [...selectedLabel],
      })
    );
  }, [contactFilter, contactFilters, filterCounter, selectedLabel]);

  async function searchContacts(signal?: AbortSignal) {
    let tagQuery = contactFilter?.tagQuery;

    const { contacts, total } = await ContactApi.read(
      {
        ...((changePage.current || updateContactsFlag) && {
          offset: page * (cachedRowsPerPage || rowsPerPage),
        }),
        limit: cachedRowsPerPage || rowsPerPage,
        ...contactFilters,
        tagQuery,
      },
      signal
    );

    return { contacts, total };
  }

  useEffect(() => {
    if (firstLoading) return;

    if (isFirstRender.current && (search || cachedSearchInput)) {
      isFirstRender.current = false;
      return;
    }

    if (isFirstRender.current && !search && !cachedSearchInput) {
      isFirstRender.current = false;
    }

    // Waiting for the cachedRowsPerPage
    setIsLoading(true);
    const controller = new AbortController();

    (async () => {
      try {
        const { total, contacts } = await searchContacts(controller.signal);

        setTotal(total);
        setContacts(contacts);
        // Reset the page
        setShowCard("");
        resetPage(!(changePage.current || updateContactsFlag));
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
    };
  }, [
    rowsPerPage,
    page,
    contactFilters,
    contactFilter,
    resetSearch,
    firstLoading,
  ]);

  function selectAllContacts() {
    setSelectedContacts(
      contacts.map((c) => {
        return c._id;
      })
    );
    setOpenDropView(false);
    setIsCheckedContactsOnPage(false);
    setIsCheckedAllContacts(true);
    setExcludeContactIds([]);
  }

  function unselectAll() {
    setSelectedContacts([]);
    setIsCheckedContactsOnPage(false);
    setIsCheckedAllContacts(false);
    setOpenDropView(false);
    setExcludeContactIds([]);
  }

  function handleShowCard(vle: string) {
    if (showCard.localeCompare(vle) === 0) setShowCard("");
    else setShowCard(vle);
  }

  useEffect(() => {
    if (isCheckedAllContacts || isCheckedContactsOnPage) {
      setSelectedContacts(
        contacts.map((c) => {
          return c._id;
        })
      );
    }
  }, [isCheckedAllContacts, isCheckedContactsOnPage, page]);

  useEffect(() => {
    if (
      router.query.search &&
      router.query.modal === "true" &&
      contacts.length > 0
    ) {
      let email = router.query.search as string;
      let foundContact = contacts.find(
        (c) => c.email === email.toLowerCase().trim()
      );

      if (foundContact) {
        modal.pushModal(
          <AddContact isEdit contact={foundContact} />,
          "Edit Contact"
        );
        router.query.modal = "false";
        router.push(router);
      }
    }
  }, [router.query.search, contacts]);

  const cachedSearchInput =
    process.browser &&
    localStorage &&
    (localStorageGetItem("SEARCH_INPUT") as string);

  return (
    <>
      <SeoHead pageName="Contacts" />

      <TopBar
        title="Contacts"
        subTitle={
          contacts
            ? contacts.length === 0
              ? "No contacts"
              : contacts.length === 1
              ? "1 contact in total"
              : contacts.length > 1
              ? `${total} contacts in total`
              : ""
            : "Loading..."
        }
      />

      <Body>
        <Header />
        <Flex>
          {isLoading || firstLoading ? (
            <div className="loading-container">
              <Loading />
            </div>
          ) : (
            <TableContainer
              isFilterOpen={!!contactFilter && !contactFilter.savedSearchName}
              showTestDrive={showTestDrive.show}
            >
              {(contacts && contacts.length) || contactsOnPage > 0 ? (
                <>
                  <TableCard>
                    <CheckboxTableTitleWrapper>
                      <div style={{ position: "relative" }}>
                        <StyledCheckBox
                          customCss={css`
                            margin-right: 15px;
                          `}
                          check={
                            isCheckedContactsOnPage || isCheckedAllContacts
                          }
                          handleCheck={() => setOpenDropView(!openDropView)}
                        />
                        {openDropView && contacts && (
                          <SelectPopover ref={selectCheckboxRef}>
                            <TitlePopover>SELECT</TitlePopover>

                            {!isCheckedContactsOnPage && (
                              <Row
                                onClick={() => {
                                  setIsCheckedAllContacts(false);
                                  setIsCheckedContactsOnPage(true);
                                  setOpenDropView(!openDropView);
                                  openSelectedContactToast(
                                    `${contacts.length} contact(s) selected`
                                  );
                                }}
                                css={isCheckedAllContacts && divider}
                              >
                                <span>
                                  {contacts.length > 1
                                    ? `Select ${contacts.length} contacts`
                                    : `Select ${contacts.length} contact`}
                                </span>
                              </Row>
                            )}

                            {!isCheckedAllContacts && (
                              <Row
                                onClick={() => {
                                  setIsCheckedContactsOnPage(false);
                                  setIsCheckedAllContacts(true);
                                  selectAllContacts();
                                  openSelectedContactToast(
                                    `All ${total} contact(s) selected`
                                  );
                                }}
                                css={divider}
                              >
                                <span>
                                  {total > 1
                                    ? `Select all ${total} contacts`
                                    : `Select all ${total} contact`}
                                </span>
                              </Row>
                            )}

                            {selectedContacts.length >= 1 && (
                              <Row onClick={unselectAll}>
                                <span>Deselect all</span>
                              </Row>
                            )}
                          </SelectPopover>
                        )}
                      </div>
                      <TableHead>
                        <FlexRow
                          onClick={() =>
                            setContactFilters((oldFilters) => ({
                              ...oldFilters,
                              createdAt: undefined,
                              email: undefined,
                              statusOrder: undefined,
                              name: oldFilters.name ? oldFilters.name * -1 : 1,
                            }))
                          }
                        >
                          CONTACT
                          <Icon src={"./icons/contacts/arrow.svg"} />
                        </FlexRow>
                      </TableHead>
                    </CheckboxTableTitleWrapper>

                    <TableHead>
                      <FlexRow>LABELS</FlexRow>
                    </TableHead>
                    <TableHead>
                      <FlexRow
                        onClick={() =>
                          setContactFilters((oldFilters) => ({
                            ...oldFilters,
                            name: undefined,
                            createdAt: undefined,
                            statusOrder: undefined,
                            email: oldFilters.email ? oldFilters.email * -1 : 1,
                          }))
                        }
                      >
                        EMAIL ADDRESS
                        <Icon src={"./icons/contacts/arrow.svg"} />
                      </FlexRow>
                    </TableHead>
                    <TableHead>
                      <FlexRow
                        onClick={() =>
                          setContactFilters((oldFilters) => ({
                            ...oldFilters,
                            phone: oldFilters.phone ? oldFilters.phone * -1 : 1,
                          }))
                        }
                      >
                        PHONE
                        <Icon src={"./icons/contacts/arrow.svg"} />
                      </FlexRow>
                    </TableHead>
                    <TableHead>
                      <FlexRow
                        onClick={() =>
                          setContactFilters((oldFilters) => ({
                            ...oldFilters,
                            createdAt: undefined,
                            email: undefined,
                            name: undefined,
                            statusOrder: oldFilters.statusOrder
                              ? oldFilters.statusOrder * -1
                              : 1,
                          }))
                        }
                        css={css`
                          :hover {
                            opacity: 1;
                          }
                        `}
                      >
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <span>STATUS </span>
                          <InfoContainer
                            onMouseEnter={() => setOpenStatusInfo(true)}
                            onMouseLeave={() => setOpenStatusInfo(false)}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              modal.pushModal(<StatusTypesModal />);
                            }}
                          >
                            <IconBerserkerMail name="info" />

                            {openStatusInfo && (
                              <Popover>
                                <PopoverTitle>EMAIL STATUS</PopoverTitle>
                                <span>
                                  <PopoverClickHere
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      modal.pushModal(<StatusTypesModal />);
                                    }}
                                  >
                                    Click here
                                  </PopoverClickHere>{" "}
                                  <span>
                                    to know the meaning
                                    <br />
                                    of these email status types
                                  </span>
                                </span>
                              </Popover>
                            )}
                          </InfoContainer>
                        </div>

                        <Icon src={"./icons/contacts/arrow.svg"} />
                      </FlexRow>
                    </TableHead>
                    <TableHead>
                      <FlexRow
                        onClick={() =>
                          setContactFilters((oldFilters) => ({
                            ...oldFilters,
                            email: undefined,
                            name: undefined,
                            statusOrder: undefined,
                            createdAt: oldFilters.createdAt
                              ? oldFilters.createdAt * -1
                              : 1,
                          }))
                        }
                      >
                        DATE CREATED
                        <Icon src={"./icons/contacts/arrow.svg"} />
                      </FlexRow>
                    </TableHead>
                    <TableActions>ACTIONS</TableActions>
                  </TableCard>
                  <>
                    {contacts &&
                      contacts.map((row) => {
                        return (
                          <TableRows
                            key={row.email}
                            row={row}
                            isShowing={showCard === row._id}
                            resetSearch={handleResetSearch}
                            handleShowCard={handleShowCard}
                            searchContacts={searchContacts}
                          />
                        );
                      })}
                  </>

                  <TableFooter
                    page={page}
                    length={total}
                    rowsPerPage={cachedRowsPerPage || rowsPerPage}
                    setRowsPerPage={(vle: number) => {
                      setRowsPerPage(vle);
                      updateCachedRowsPerPage(vle);
                    }}
                    handlePage={handlePage}
                    handleContactsPerPage={(pages: number) => {
                      setContactsOnPage(pages);
                    }}
                  />
                </>
              ) : (!!cachedSearchInput && contacts.length <= 0) ||
                totalFilterAppliedCount > 0 ||
                filterByEmail.length > 0 ? (
                <NoContactsWrapper>
                  <img src="./icons/empty-screen.svg" alt="No contacts" />
                  <div className="Title">No contacts to show</div>
                  <div className="Text">
                    Sorry, we can't find contacts matching your criteria.
                  </div>
                </NoContactsWrapper>
              ) : (
                <NoContactsWrapper>
                  <img src="./icons/empty-screen.svg" alt="No contacts" />
                  <div className="Title">Empty contact list</div>
                  <div className="Text">
                    Your contact list is empty. Start sending email broadcast to
                    your contacts. Would you like to add a contact?
                  </div>
                  <StyledButton
                    onClick={() => {
                      modal.pushModal(<ImportContact />, "");
                    }}
                    inverted
                    css={css`
                      width: 320px;
                      margin-top: 40px;
                      margin-bottom: 8px;
                    `}
                  >
                    Import Contacts
                  </StyledButton>
                  <StyledButton
                    onClick={() => {
                      modal.pushModal(<AddContact />, "Add new contact");
                    }}
                    css={css`
                      width: 320px;
                    `}
                  >
                    Add A Contact Manually
                  </StyledButton>
                </NoContactsWrapper>
              )}
            </TableContainer>
          )}
        </Flex>
      </Body>
      <SidebarFilters />
    </>
  );
}
